import groupBy from 'lodash/groupBy';
import lodashRange from 'lodash/range';
import sortBy from 'lodash/sortBy';

import { COMMENT_SIGNATURE } from '@/constants';
import type { TLAITextEdit } from '@/types/api';

type RangeKey = { start: number; end: number };

const MAX_LINE_LENGTH = 255;

export function isSignatureCommentLine(line: string, commentPostFix: string | null): boolean {
  if (!commentPostFix) return false;
  return line.trimStart().startsWith('*') && line.includes(`" ${commentPostFix}`);
}

/**
 * Check if the previous line is a signature comment that matches the current line's statement.
 * This prevents treating unrelated signature comments as if they were for the current line.
 */
export function hasPreviousSignatureCommentForLine(
  prevLine: string,
  currentLine: string,
  commentPostFix: string | null,
): boolean {
  if (!commentPostFix || !isSignatureCommentLine(prevLine, commentPostFix)) {
    return false;
  }

  const uncommented = uncommentSignatureLine(prevLine, commentPostFix);
  if (!uncommented) return false;

  const prevKey = statementKey(uncommented);
  const currentKey = statementKey(currentLine);

  return prevKey !== '' && prevKey === currentKey;
}

function uncommentSignatureLine(line: string, commentPostFix: string | null): string | null {
  if (!commentPostFix || !isSignatureCommentLine(line, commentPostFix)) return null;

  const signatureToken = `" ${commentPostFix}`;
  const sigIdx = line.indexOf(signatureToken);
  if (sigIdx === -1) return null;

  const beforeSig = line.slice(0, sigIdx).trimStart();
  if (!beforeSig.startsWith('*')) return null;

  return beforeSig.startsWith('* ') ? beforeSig.slice(2) : beforeSig.slice(1);
}

function statementKey(line: string): string {
  const trimmed = line.trimStart();
  if (!trimmed) return '';
  const parts = trimmed.split(/\s+/);
  return `${parts[0] ?? ''} ${parts[1] ?? ''}`.trim();
}

function allCommentsMatchStatement(commentRun: string[], targetKey: string, commentPostFix: string | null): boolean {
  return commentRun.every((cLine) => {
    const uncommented = uncommentSignatureLine(cLine, commentPostFix);
    return uncommented && statementKey(uncommented) === targetKey;
  });
}

function shouldCollapseCommentRun(commentRun: string[], nextLiveLine: string, commentPostFix: string | null): boolean {
  if (commentRun.length <= 1) return false;

  const nextKey = statementKey(nextLiveLine);
  if (!nextKey) return false;

  return allCommentsMatchStatement(commentRun, nextKey, commentPostFix);
}

export function collapseSignatureCommentHistory(lines: string[], commentPostFix: string | null): string[] {
  if (!commentPostFix) return lines;

  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!isSignatureCommentLine(line, commentPostFix)) {
      out.push(line);
      i += 1;
      continue;
    }

    const runStart = i;
    while (i < lines.length && isSignatureCommentLine(lines[i], commentPostFix)) i += 1;
    const commentRun = lines.slice(runStart, i);

    if (i >= lines.length) {
      out.push(...commentRun);
      continue;
    }

    const nextLiveLine = lines[i];
    if (shouldCollapseCommentRun(commentRun, nextLiveLine, commentPostFix)) {
      out.push(commentRun[0]);
    } else {
      out.push(...commentRun);
    }
  }

  return out;
}

function normalizeEditRange(edit: TLAITextEdit): { from: number; to: number } {
  return {
    from: Math.min(edit.from, edit.to),
    to: Math.max(edit.from, edit.to),
  };
}

function shouldCommentLine(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.length > 0 && !trimmed.startsWith('*') && !trimmed.startsWith('"');
}

export function commentLine(line: string, postFix?: string | null): string {
  if (!shouldCommentLine(line)) return line;

  const commented = `* ${line}`;
  // Check if the line already has the suffix to avoid duplicating it
  if (postFix && line.includes(`" ${postFix}`)) {
    return commented;
  }
  return postFix ? `${commented} " ${postFix}` : commented;
}

/**
 * Add comment suffix to a line if not already present
 * Skips lines that are already comments (starting with * or ")
 */
export function addCommentSuffix(line: string, commentPostFix: string | null): string {
  if (!commentPostFix) return line;

  const trimmedLine = line.trim();
  const startsWithComment = line.trimStart().startsWith('*') || line.trimStart().startsWith('"');
  const alreadyHasSuffix = line.includes(`" ${commentPostFix}`);

  if (trimmedLine.length > 0 && !startsWithComment && !alreadyHasSuffix) {
    return `${line} " ${commentPostFix}`;
  }

  return line;
}

export function applyTextEditsToCode(
  edits: TLAITextEdit[],
  code: string,
  commentPostFix: string | null = COMMENT_SIGNATURE,
  skipCommenting: boolean = false,
): string {
  if (edits.length === 0) return code;

  const originalLines = code.split('\n');

  // Build line start and end offsets for binary search (precomputed for efficiency)
  const lineStartOffsets: number[] = [];
  const lineEndOffsets: number[] = [];
  let totalOffset = 0;
  for (let i = 0; i < originalLines.length; i++) {
    lineStartOffsets[i] = totalOffset;
    const lineLength = originalLines[i].length;
    const newlineLength = i < originalLines.length - 1 ? 1 : 0;
    totalOffset += lineLength + newlineLength;
    lineEndOffsets[i] = totalOffset - newlineLength; // End of line (excluding newline)
  }

  const findLineIndex = (offset: number): number => {
    if (offset < 0) return 0;
    if (offset >= totalOffset) return originalLines.length - 1;
    let left = 0;
    let right = originalLines.length - 1;
    while (left < right) {
      const mid = Math.floor((left + right) / 2);
      const lineStart = lineStartOffsets[mid];
      const lineEnd = lineEndOffsets[mid];
      if (offset < lineStart) right = mid - 1;
      else if (offset > lineEnd) left = mid + 1;
      else return mid;
    }
    return left;
  };

  // Group edits by line range
  const editsWithRanges = edits.map((edit) => {
    const { from, to } = normalizeEditRange(edit);
    const startLineIdx = findLineIndex(from);
    const endLineIdx = findLineIndex(to);
    const key = `${startLineIdx}-${endLineIdx}`;
    return { edit, key, range: { start: startLineIdx, end: endLineIdx } };
  });

  const editsByRange = groupBy(editsWithRanges, 'key');
  const rangeKeys = new Map<string, RangeKey>();
  for (const { key, range: rangeKey } of editsWithRanges) {
    if (!rangeKeys.has(key)) {
      rangeKeys.set(key, rangeKey);
    }
  }

  // NEW: Identify statement blocks affected by edits
  // This allows us to comment entire blocks instead of individual lines
  const affectedBlocks = new Map<string, { start: number; end: number; keyword: string; editLines: Set<number> }>();

  for (const { range } of editsWithRanges) {
    for (let lineIdx = range.start; lineIdx <= range.end; lineIdx++) {
      // Skip lines that are already commented with signatures - these are from previous edits
      const line = originalLines[lineIdx];
      const isCommented = line.trimStart().startsWith('*');
      const hasSig = commentPostFix && line.includes(`" ${commentPostFix}`);

      if (isCommented && hasSig) {
        continue; // Don't process commented lines with signatures as blocks
      }

      const block = findBlockForLine(lineIdx, originalLines, commentPostFix);
      if (block && !skipCommenting) {
        const blockKey = `${block.start}-${block.end}`;
        if (!affectedBlocks.has(blockKey)) {
          affectedBlocks.set(blockKey, { ...block, editLines: new Set() });
        }
        // Track which lines in the block were edited
        for (let i = range.start; i <= range.end; i++) {
          if (i >= block.start && i <= block.end) {
            affectedBlocks.get(blockKey)!.editLines.add(i);
          }
        }
      }
    }
  }

  const linesToComment = new Set<number>();
  const replacementsByEndLine = new Map<number, string[]>();
  const seenReplacements = new Set<string>();

  // Sort ranges bottom-to-top (highest end line first)
  const sortedRanges = sortBy(Array.from(rangeKeys.entries()), [(entry) => -entry[1].end]);

  for (const [key, rangeKey] of sortedRanges) {
    const { start: startLineIdx, end: endLineIdx } = rangeKey;
    const rangeEdits = editsByRange[key].map((item) => item.edit);

    // Mark affected lines for commenting
    for (const i of lodashRange(startLineIdx, endLineIdx + 1)) {
      linesToComment.add(i);
    }

    // Get original text for this range
    const originalRangeText = originalLines.slice(startLineIdx, endLineIdx + 1).join('\n');
    const rangeStartOffset = lineStartOffsets[startLineIdx];

    // Apply edits bottom-to-top
    let modifiedText = originalRangeText;
    const sortedEdits = sortBy(rangeEdits, [(edit) => -normalizeEditRange(edit).to]);

    for (const edit of sortedEdits) {
      const { from, to } = normalizeEditRange(edit);
      const relativeFrom = from - rangeStartOffset;
      const relativeTo = to - rangeStartOffset;

      let replacement = edit.replacement;
      const textBefore = modifiedText.slice(0, relativeFrom);
      const textAfter = modifiedText.slice(relativeTo);

      // If replacement is a comment and we're mid-line, ensure it's on a new line
      if (replacement.trim().startsWith('*') && relativeFrom > 0 && !textBefore.endsWith('\n')) {
        const lastNewlineIdx = textBefore.lastIndexOf('\n');
        const currentLineContent = textBefore.slice(lastNewlineIdx + 1);
        if (currentLineContent.trim().length > 0) {
          replacement = `\n${replacement}`;
        }
      }

      modifiedText = textBefore + replacement + textAfter;
    }

    // Extract only new content (remove original first line if it matches)
    const finalLines = modifiedText.split('\n');
    if (finalLines.length > 0 && finalLines[0] === originalLines[startLineIdx]) {
      finalLines.shift();
    }

    const replacementKey = finalLines.join('\n');
    if (finalLines.length > 0 && !seenReplacements.has(replacementKey)) {
      seenReplacements.add(replacementKey);
      replacementsByEndLine.set(endLineIdx, finalLines);
    }
  }

  const result: string[] = [];
  const processedBlocks = new Set<string>();

  // Precompute a lookup map from block start line to block key for O(1) access
  const blocksByStart = new Map<number, string>();
  for (const [key, block] of affectedBlocks.entries()) {
    blocksByStart.set(block.start, key);
  }

  for (let i = 0; i < originalLines.length; i++) {
    // NEW: Check if this line is the start of an affected block using O(1) lookup
    const blockKey = blocksByStart.get(i);

    if (blockKey && !processedBlocks.has(blockKey)) {
      // Process entire block at once
      const block = affectedBlocks.get(blockKey)!;
      processedBlocks.add(blockKey);

      // Comment the entire original block
      for (let j = block.start; j <= block.end; j++) {
        result.push(commentLine(originalLines[j], commentPostFix));
      }

      // Now output the modified block
      // We need to apply edits to the block and output it
      const blockText = originalLines.slice(block.start, block.end + 1).join('\n');
      const blockStartOffset = lineStartOffsets[block.start];

      // Find all edits that affect this block
      const blockEdits = edits.filter((edit) => {
        const { from, to } = normalizeEditRange(edit);
        const editStart = findLineIndex(from);
        const editEnd = findLineIndex(to);
        return editStart >= block.start && editEnd <= block.end;
      });

      // Apply edits to the block
      let modifiedBlock = blockText;
      const sortedBlockEdits = sortBy(blockEdits, [(edit) => -normalizeEditRange(edit).to]);

      for (const edit of sortedBlockEdits) {
        const { from, to } = normalizeEditRange(edit);
        const relativeFrom = from - blockStartOffset;
        const relativeTo = to - blockStartOffset;
        const textBefore = modifiedBlock.slice(0, relativeFrom);
        const textAfter = modifiedBlock.slice(relativeTo);
        modifiedBlock = textBefore + edit.replacement + textAfter;
      }

      // Output modified block with signature on ALL lines
      const modifiedLines = modifiedBlock.split('\n');
      for (let j = 0; j < modifiedLines.length; j++) {
        result.push(skipCommenting ? modifiedLines[j] : addCommentSuffix(modifiedLines[j], commentPostFix));
      }

      // Skip to end of block
      i = block.end;
      continue;
    }

    // EXISTING: Process non-block lines as before
    const line = originalLines[i];
    const wasChanged = linesToComment.has(i);

    // Check if there's already a signature comment for this line above it
    let hasPreviousSignatureComment = false;
    if (i > 0 && commentPostFix) {
      const prevLine = originalLines[i - 1];
      hasPreviousSignatureComment = hasPreviousSignatureCommentForLine(prevLine, line, commentPostFix);
    }

    // If skipCommenting is true and line was changed, don't include the old line at all
    // Also skip if the line is already a signature comment line (avoid double-commenting)
    // Also skip if there's already a signature comment on the previous line
    if (!wasChanged || !skipCommenting) {
      const isAlreadyCommented = isSignatureCommentLine(line, commentPostFix);
      const shouldComment = wasChanged && !isAlreadyCommented && !hasPreviousSignatureComment;

      if (shouldComment) {
        result.push(commentLine(line, commentPostFix));
      } else if (!wasChanged || isAlreadyCommented) {
        result.push(line);
      }
      // If wasChanged && hasPreviousSignatureComment && !isAlreadyCommented, skip the line entirely
    }

    const replacement = replacementsByEndLine.get(i);
    if (replacement && replacement.length > 0) {
      // Add comment suffix to each replacement line if not already present (unless skipCommenting is true)
      const replacementWithSuffix = skipCommenting
        ? replacement
        : replacement.map((repLine) => addCommentSuffix(repLine, commentPostFix));
      result.push(...replacementWithSuffix);
    }
  }

  const collapsed = collapseSignatureCommentHistory(result, commentPostFix).join('\n');
  const withoutDuplicates = removeDuplicateSignatures(collapsed, commentPostFix);
  const brokenLines = breakLongLines(withoutDuplicates, commentPostFix);
  return alignCommentSignature(brokenLines, commentPostFix);
}

/**
 * Remove duplicate signature suffixes from each line
 */
function removeDuplicateSignatures(code: string, commentPostFix: string | null): string {
  if (!commentPostFix) return code;

  const lines = code.split('\n');
  const signaturePattern = `" ${commentPostFix}`;

  const cleanedLines = lines.map((line) => {
    // Count how many times the signature appears
    const matches = line.split(signaturePattern);
    if (matches.length <= 2) {
      // 0 or 1 signature, no duplicates
      return line;
    }

    // Multiple signatures found - keep only the first one
    // Join first part with signature, discard the rest
    return matches[0] + signaturePattern;
  });

  return cleanedLines.join('\n');
}

/**
 * Break lines that exceed ABAP's 255 character limit
 */
function breakLongLines(code: string, commentPostFix: string | null): string {
  const lines = code.split('\n');
  const result: string[] = [];

  for (const line of lines) {
    if (line.length <= MAX_LINE_LENGTH) {
      result.push(line);
      continue;
    }

    // Line exceeds limit, need to break it
    const isCommented = line.trimStart().startsWith('*');
    const baseIndent = line.match(/^(\s*)/)?.[1] || '';

    // Extract content and preserve indentation that comes AFTER the * for commented lines
    let content: string;
    let linePrefix: string;
    let continuationIndent: string;

    if (isCommented) {
      const starPos = line.indexOf('*');
      const afterStar = line.substring(starPos + 1);
      // Preserve indentation after * (e.g., "*     SELECT" keeps the spaces)
      const indentAfterStar = afterStar.match(/^(\s*)/)?.[1] || '';
      content = afterStar.trimStart();
      linePrefix = `${baseIndent}*${indentAfterStar}`;
      continuationIndent = `${baseIndent}*       `;
    } else {
      content = line.trimStart();
      const originalIndent = line.match(/^(\s*)/)?.[1] || '';
      linePrefix = originalIndent;
      continuationIndent = `${originalIndent}      `;
    }

    // Check if line has signature at the end
    const signaturePattern = commentPostFix ? `" ${commentPostFix}` : null;
    let signature = '';
    let contentWithoutSig = content;

    if (signaturePattern && content.endsWith(signaturePattern)) {
      signature = signaturePattern;
      contentWithoutSig = content.substring(0, content.length - signaturePattern.length).trimEnd();
    }

    // For commented lines, ALL lines should have signature
    // For live lines, only the changed line has signature (which is the original line being broken)
    const signatureOnAllLines = isCommented && signature;
    const signatureLength = signatureOnAllLines ? signature.length : 0;

    const availableFirstLine = MAX_LINE_LENGTH - linePrefix.length - signatureLength;
    const availableContinuation = MAX_LINE_LENGTH - continuationIndent.length - signatureLength;

    // Find good breaking points: commas, AND, OR, etc.
    const breakPoints = [', ', ' AND ', ' OR ', ' = ', ' + ', ' - ', ' * ', ' / '];

    let remaining = contentWithoutSig;
    let isFirstLine = true;

    while (remaining.length > 0) {
      const availableSpace = isFirstLine ? availableFirstLine : availableContinuation;
      const currentIndent = isFirstLine ? linePrefix : continuationIndent;

      if (remaining.length <= availableSpace) {
        // Remaining content fits
        const lineSuffix = signatureOnAllLines ? signature : isFirstLine ? signature : '';
        result.push(currentIndent + remaining + lineSuffix);
        break;
      }

      // Need to break - find best breaking point before availableSpace
      let breakPos = -1;
      for (const bp of breakPoints) {
        const lastOccurrence = remaining.lastIndexOf(bp, availableSpace);
        if (lastOccurrence > breakPos && lastOccurrence > 0) {
          breakPos = lastOccurrence + bp.length;
        }
      }

      if (breakPos === -1) {
        // No good break point found, force break at available space
        breakPos = availableSpace;
      }

      const chunk = remaining.substring(0, breakPos).trimEnd();
      remaining = remaining.substring(breakPos).trimStart();

      // Add signature to this chunk if it's a commented line (all lines get signature)
      const lineSuffix = signatureOnAllLines ? signature : '';
      result.push(currentIndent + chunk + lineSuffix);
      isFirstLine = false;
    }
  }

  return result.join('\n');
}

// Keywords that start statement blocks
const BLOCK_KEYWORDS = ['SELECT', 'INSERT', 'UPDATE', 'DELETE', 'MODIFY', 'LOOP', 'IF', 'CASE', 'DO', 'WHILE', 'CALL'];

function getFirstKeyword(line: string): string | null {
  const trimmed = line.trimStart().replace(/^\*\s*/, ''); // Remove leading comment
  const match = trimmed.match(/^(\w+)/);
  return match ? match[1].toUpperCase() : null;
}

function isBlockStart(line: string): boolean {
  const keyword = getFirstKeyword(line);
  return keyword !== null && BLOCK_KEYWORDS.includes(keyword);
}

/**
 * Find the statement block that contains the given line index.
 * Returns null if the line is not part of a statement block.
 */
function findBlockForLine(
  lineIdx: number,
  lines: string[],
  commentPostFix: string | null,
): { start: number; end: number; keyword: string } | null {
  if (lineIdx < 0 || lineIdx >= lines.length) return null;

  // Start by finding the block start - scan backward from lineIdx
  let blockStartIdx = lineIdx;

  // If current line is a block start, use it
  if (isBlockStart(lines[lineIdx])) {
    blockStartIdx = lineIdx;
  } else {
    // Scan backward to find the block start
    // We need to skip over COMPLETE commented blocks (all lines with signatures)
    // but include lines that are part of the live block structure
    let found = false;

    for (let i = lineIdx - 1; i >= 0; i--) {
      const line = lines[i];
      const isCommented = line.trimStart().startsWith('*');
      const hasSig = commentPostFix && line.includes(`" ${commentPostFix}`);

      // Skip over old commented lines
      if (isCommented && hasSig) {
        continue;
      }

      // Stop at user comments (commented without signature) or empty lines
      if ((isCommented && !hasSig) || line.trim() === '') {
        break;
      }

      // Check for statement end (would indicate previous statement)
      if (isStatementEnd(line, commentPostFix)) {
        break;
      }

      if (isBlockStart(line)) {
        blockStartIdx = i;
        found = true;
        break;
      }
    }

    if (!found) {
      return null;
    }
  }

  const blockKeyword = getFirstKeyword(lines[blockStartIdx]);
  if (!blockKeyword) return null;

  // Check if this is a single-line statement
  if (isStatementEnd(lines[blockStartIdx], commentPostFix)) {
    return { start: blockStartIdx, end: blockStartIdx, keyword: blockKeyword };
  }

  // Find the block end - scan forward from blockStartIdx
  // Skip over COMPLETE commented blocks but include live lines
  let blockEndIdx = blockStartIdx;
  for (let i = blockStartIdx + 1; i < lines.length; i++) {
    const line = lines[i];
    const isCommented = line.trimStart().startsWith('*');
    const hasSig = commentPostFix && line.includes(`" ${commentPostFix}`);

    // Skip over commented lines with signatures (old commented blocks)
    if (isCommented && hasSig) {
      continue;
    }

    // Stop at user comments (commented without signature)
    if (isCommented && !hasSig) {
      break;
    }

    // Stop at another block start (different block)
    if (isBlockStart(line)) {
      const lineKeyword = getFirstKeyword(line);
      if (lineKeyword !== blockKeyword) {
        break;
      }
    }

    blockEndIdx = i;

    // Check if statement ends here
    if (isStatementEnd(line, commentPostFix)) {
      break;
    }
  }

  return { start: blockStartIdx, end: blockEndIdx, keyword: blockKeyword };
}

function isStatementEnd(line: string, commentPostFix: string | null): boolean {
  const trimmed = line.trim();

  // Check if line ends with period (directly)
  if (trimmed.endsWith('.')) return true;

  // Check if line ends with signature comment (and period before it)
  if (commentPostFix) {
    const signaturePattern = `" ${commentPostFix}`;
    if (trimmed.endsWith(signaturePattern)) {
      // Remove signature and check if what's before ends with period
      const withoutSig = trimmed.substring(0, trimmed.length - signaturePattern.length).trim();
      return withoutSig.endsWith('.');
    }
  }

  // Check if line has an inline comment (starts with ") and there's a period before the comment
  // Example: "ORDER BY PRIMARY KEY.    "ATC priority 1 CR553333"
  const inlineCommentMatch = trimmed.match(/^(.+?)(\s+"[^"]*"?\s*)$/);
  if (inlineCommentMatch) {
    const beforeComment = inlineCommentMatch[1].trim();
    if (beforeComment.endsWith('.')) return true;
  }

  return false;
}

const TAB_SIZE = 8;

/**
 * Calculate visual column position accounting for tabs
 */
function getVisualColumn(text: string): number {
  let column = 0;
  for (const char of text) {
    if (char === '\t') {
      column = Math.floor(column / TAB_SIZE) * TAB_SIZE + TAB_SIZE;
    } else {
      column++;
    }
  }
  return column;
}

/**
 * Align all inline comments containing COMMENT_SIGNATURE to the rightmost comment position
 */
export function alignCommentSignature(code: string, commentSignature: string | null = COMMENT_SIGNATURE): string {
  if (!commentSignature) return code;

  const lines = code.split('\n');
  const commentPattern = `" ${commentSignature}`;

  const commentLines = lines
    .map((line, index) => {
      const commentIndex = line.indexOf(commentPattern);
      if (commentIndex === -1) return null;

      const beforeComment = line.substring(0, commentIndex);
      const trimmedBeforeComment = beforeComment.replace(/\s+$/, '');
      const visualColumn = getVisualColumn(trimmedBeforeComment);

      return {
        lineIndex: index,
        commentStartPos: commentIndex,
        visualColumn,
        trimmedBeforeComment,
        comment: line.substring(commentIndex),
      };
    })
    .filter((line): line is NonNullable<typeof line> => line !== null);

  if (commentLines.length === 0) return code;

  const maxVisualColumn = Math.max(...commentLines.map((cl) => cl.visualColumn));
  const targetVisualColumn = maxVisualColumn + 1;

  const formattedLines = [...lines];
  for (const { lineIndex, visualColumn, trimmedBeforeComment, comment } of commentLines) {
    // Calculate how much space we can add without exceeding max length
    const maxSpacesToAdd = MAX_LINE_LENGTH - trimmedBeforeComment.length - comment.length;
    const desiredSpacesToAdd = Math.max(1, targetVisualColumn - visualColumn);
    const spacesToAdd = Math.min(desiredSpacesToAdd, maxSpacesToAdd);

    // Ensure at least one space but don't exceed max length
    if (spacesToAdd >= 1) {
      formattedLines[lineIndex] = trimmedBeforeComment + ' '.repeat(spacesToAdd) + comment;
    } else {
      // Line is already too long, don't add any alignment spaces
      formattedLines[lineIndex] = trimmedBeforeComment + comment;
    }
  }

  return formattedLines.join('\n');
}
